import React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import HubSectionContainer from '../CustomContainer';
import { loadLesson, LessonView } from '../../Lesson';
import useHubPageView from '../useHubPageView';
import useLessonBookmarks from './useLessonBookmarks';
import LessonActionBar from './LessonActionBar';
import LessonsHeader from './LessonsHeader';
import BookmarkFeedback from './BookmarkFeedback';
import useRecordLessonView from './useRecordLessonView';

const GRID_PATH = '/hub/lessons-and-guides';

interface LessonNavState { from?: string; fromWhere?: string }

export default function LessonDetail() {
  const { slug = '' } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const bookmarkApi = useLessonBookmarks();
  const { isBookmarked, toggle } = bookmarkApi;

  const lesson = React.useMemo(() => loadLesson(slug), [slug]);
  const state = (location.state || {}) as LessonNavState;
  const fromWhere = state.fromWhere || 'direct_link';

  // Held until the pet parent resolves so the event is attributed — see
  // useHubPageView. Lesson content comes from the bundled catalog, so without the
  // gate this fired before identify() on every direct entry from Zendesk or email.
  useHubPageView(
    { lesson_name: lesson?.title, from_where: fromWhere },
    [lesson?.slug, fromWhere],
    !!lesson,
  );

  // A learning.petcademy.org link with a ?t= token hands off here with ?from=learning
  // (see PublicLessonPage); everything else is a read inside the Hub.
  const viewSource = new URLSearchParams(location.search).get('from') === 'learning' ? 'learning' : 'hub';
  useRecordLessonView(lesson?.slug, viewSource);

  // A new lesson (or a related-lesson hop) should start at the top, not keep the
  // previous page's scroll position.
  React.useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  // Deep links from Zendesk or email have no originating page, so fall back to
  // the grid rather than dead-ending — QA 10.4.
  const onBack = () => navigate(state.from || GRID_PATH);

  if (!lesson) {
    return (
      <Box>
        <LessonsHeader />
        <HubSectionContainer wide sx={{ py: 6 }}>
          <Typography>We couldn&apos;t find that lesson.</Typography>
        </HubSectionContainer>
      </Box>
    );
  }

  return (
    <Box>
      <LessonsHeader />
      <LessonActionBar
        title={lesson.title}
        bookmarked={isBookmarked(lesson.slug)}
        onBack={onBack}
        onToggleBookmark={() => toggle(lesson.slug, lesson.title)}
      />
      <LessonView
        lesson={lesson}
        onNavigate={(next) => navigate(`${GRID_PATH}/${next}`, {
          state: { from: `${GRID_PATH}/${slug}`, fromWhere: 'related_lesson' },
        })}
      />
      <BookmarkFeedback bookmarks={bookmarkApi} />
    </Box>
  );
}
